import React, { useState, useEffect, useCallback, createContext, useContext, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import "@/App.css";
import { motion, AnimatePresence } from "framer-motion";
import useEmblaCarousel from "embla-carousel-react";
import { Phone, MessageCircle, MapPin, Menu, X, ChevronDown, Mail, ArrowLeft } from "lucide-react";
// Secondary pages are split into their own chunks so the homepage loads less JavaScript
const AdminLogin = lazy(() => import("./components/AdminLogin"));
const AdminPanel = lazy(() => import("./components/AdminPanel"));
const RimborsoCassaMalattia = lazy(() => import("./components/RimborsoCassaMalattia"));
const DoveMiTrovate = lazy(() => import("./components/DoveMiTrovate"));
const BuoniRegalo = lazy(() => import("./components/BuoniRegalo"));
const VerifyVoucher = lazy(() => import("./components/VerifyVoucher"));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-pink-50">
    <div className="w-12 h-12 border-4 border-pink-200 border-t-pink-600 rounded-full animate-spin"></div>
  </div>
);
import VoucherBanner from "./components/VoucherBanner";
import { setPageMeta, DEFAULT_META, DEFAULT_META_DE } from "./utils/seo";
import faqData from "./data/faq.json";
import legalData from "./data/legal.json";

// Official Logo
const LOGO_URL = "/logo-emi-harmony.png";

// Contact Info (obfuscated to prevent bot scraping)
const _c = (arr) => arr.map(c => String.fromCharCode(c)).join('');
const CONTACT = {
  get phone() { return _c([43,52,49,32,55,54,32,54,57,51,32,51,56,32,48,49]); },
  get phoneLink() { return _c([43,52,49,55,54,54,57,51,51,56,48,49]); },
  get whatsapp() { return _c([52,49,55,54,54,57,51,51,56,48,49]); },
  get email() { return _c([105,110,102,111,64,101,109,105,45,104,97,114,109,111,110,121,46,99,104]); },
  address: {
    it: "Via alla Bolla 18, 6595 Riazzino, Svizzera",
    de: "Via alla Bolla 18, 6595 Riazzino, Schweiz"
  }
};

// Default images hosted on Cloudinary (permanent CDN)
const defaultImages = {
  hero_medical: "https://res.cloudinary.com/dbbtcjtb5/image/upload/v1774529542/emi-harmony/hero_medical.jpg",
  hero_relax: "https://res.cloudinary.com/dbbtcjtb5/image/upload/v1774529544/emi-harmony/hero_relax.jpg",
  service_facial: "https://res.cloudinary.com/dbbtcjtb5/image/upload/v1774529545/emi-harmony/service_facial.jpg",
  service_lomi: "https://res.cloudinary.com/dbbtcjtb5/image/upload/v1774529546/emi-harmony/service_lomi.jpg",
  service_plantar: "https://res.cloudinary.com/dbbtcjtb5/image/upload/v1774529548/emi-harmony/service_plantar.jpg",
  service_hotstone: "https://res.cloudinary.com/dbbtcjtb5/image/upload/v1774529549/emi-harmony/service_hotstone.jpg",
  profile: "https://res.cloudinary.com/dbbtcjtb5/image/upload/v1774529542/emi-harmony/hero_medical.jpg",
};

// Cloudinary delivery: automatic format (WebP/AVIF) and quality at the requested width.
// Non-Cloudinary URLs are returned unchanged.
const cld = (url, width) =>
  url && url.includes("res.cloudinary.com/") && url.includes("/image/upload/")
    ? url.replace("/image/upload/", `/image/upload/f_auto,q_auto,w_${width},c_limit/`)
    : url;
const cldSrcSet = (url, widths) =>
  url && url.includes("res.cloudinary.com/") ? widths.map((w) => `${cld(url, w)} ${w}w`).join(", ") : undefined;

// API - Use relative URL in production, env var in development
const API_URL = process.env.REACT_APP_BACKEND_URL 
  ? process.env.REACT_APP_BACKEND_URL + '/api'
  : '/api';

// Content Context
const ContentContext = createContext();

// Images Context
const ImagesContext = createContext(defaultImages);

// Language Context
const LanguageContext = createContext();

// Pages that exist in German under /de (keep in sync with backend/seo_meta.py).
// Every other page is Italian-only and keeps its URL whatever the chosen language.
const LOCALIZED_PATHS = ['/', '/dove-mi-trovate', '/buoni-regalo', '/privacy', '/impressum'];
const stripDe = (path) => (path === '/de' ? '/' : path.startsWith('/de/') ? path.slice(3) : path);
const toDe = (path) => (path === '/' ? '/de' : '/de' + path);

export function LanguageProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const isDePath = location.pathname === '/de' || location.pathname.startsWith('/de/');
  const basePath = stripDe(location.pathname);
  const isLocalized = LOCALIZED_PATHS.includes(basePath);
  const [preferred, setPreferred] = useState(isDePath ? 'de' : 'it');
  const language = isLocalized ? (isDePath ? 'de' : 'it') : preferred;

  useEffect(() => {
    if (isLocalized) setPreferred(isDePath ? 'de' : 'it');
  }, [isLocalized, isDePath]);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  // URL of an internal page in the current language
  const localePath = useCallback(
    (path) => (language === 'de' && LOCALIZED_PATHS.includes(path) ? toDe(path) : path),
    [language]
  );

  const setLanguage = (lang) => {
    setPreferred(lang);
    if (!isLocalized) return;
    const target = lang === 'de' ? toDe(basePath) : basePath;
    if (target !== location.pathname) navigate(target + location.hash);
  };

  const toggleLanguage = () => setLanguage(language === 'it' ? 'de' : 'it');

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, localePath, basePath }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

// Header Component
const Header = () => {
  const { language, setLanguage, localePath, basePath } = useLanguage();
  const content = useContext(ContentContext);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navLabels = {
    it: { services: 'Servizi', packages: 'Pacchetti', about: 'Chi Sono', rimborso: 'Rimborso Cassa Malattia', location: 'Lo Studio', buoni: 'Buoni Regalo' },
    de: { services: 'Dienstleistungen', packages: 'Pakete', about: 'Über Mich', rimborso: 'Krankenkassen-Rückerstattung', location: 'Das Studio', buoni: 'Geschenkgutscheine' }
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    if (basePath !== '/') {
      window.location.href = localePath('/') + '#' + id;
      return;
    }
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? "bg-white shadow-md py-3" 
          : "bg-white/80 backdrop-blur-md py-4"
      }`}
    >
      <div className="container-custom flex items-center justify-between">
        <Link 
          to={localePath("/")}
          data-testid="logo"
          className="flex items-center"
        >
          <img src={LOGO_URL} alt="EMI Harmony Center" className="h-[52px] md:h-[62px] w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-6">
          {['services', 'packages', 'about'].map(section => (
            <button 
              key={section}
              data-testid={`nav-${section}`}
              onClick={() => scrollToSection(section)}
              className="text-neutral-700 hover:text-pink-700 transition-colors font-medium text-sm"
            >
              {navLabels[language][section]}
            </button>
          ))}
          <Link
            to={localePath("/rimborso")}
            data-testid="nav-rimborso"
            className={`text-sm font-medium transition-colors ${location.pathname === '/rimborso' ? 'text-pink-700' : 'text-neutral-700 hover:text-pink-700'}`}
          >
            {navLabels[language].rimborso}
          </Link>
          <Link
            to={localePath("/dove-mi-trovate")}
            data-testid="nav-location"
            className={`text-sm font-medium transition-colors ${location.pathname === '/dove-mi-trovate' ? 'text-pink-700' : 'text-neutral-700 hover:text-pink-700'}`}
          >
            {navLabels[language].location}
          </Link>
          <Link
            to={localePath("/buoni-regalo")}
            data-testid="nav-buoni"
            className={`text-sm font-medium transition-colors inline-flex items-center gap-1 ${location.pathname === '/buoni-regalo' ? 'text-pink-700' : 'text-neutral-700 hover:text-pink-700'}`}
          >
            <span className="text-pink-600">♥</span>
            {navLabels[language].buoni}
          </Link>
        </nav>

        <div className="flex items-center gap-4">
          <div className="lang-switch" data-testid="language-switcher">
            <button 
              data-testid="lang-it"
              onClick={() => setLanguage('it')}
              className={language === 'it' ? 'active' : ''}
            >
              IT
            </button>
            <span className="text-neutral-300">|</span>
            <button 
              data-testid="lang-de"
              onClick={() => setLanguage('de')}
              className={language === 'de' ? 'active' : ''}
            >
              DE
            </button>
          </div>

          <button 
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 text-neutral-700"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-pink-100"
          >
            <nav className="container-custom py-4 flex flex-col gap-4">
              {['services', 'packages', 'about'].map(section => (
                <button 
                  key={section}
                  onClick={() => scrollToSection(section)}
                  className="text-left text-neutral-700 hover:text-pink-700 py-2"
                >
                  {navLabels[language][section]}
                </button>
              ))}
              <Link
                to={localePath("/rimborso")}
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-left text-neutral-700 hover:text-pink-700 py-2"
              >
                {navLabels[language].rimborso}
              </Link>
              <Link
                to={localePath("/dove-mi-trovate")}
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-left text-neutral-700 hover:text-pink-700 py-2"
              >
                {navLabels[language].location}
              </Link>
              <Link
                to={localePath("/buoni-regalo")}
                onClick={() => setIsMobileMenuOpen(false)}
                data-testid="mobile-nav-buoni"
                className="text-left text-pink-700 hover:text-pink-900 py-2 font-medium inline-flex items-center gap-2"
              >
                <span>♥</span> {navLabels[language].buoni}
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  const { language } = useLanguage();
  const content = useContext(ContentContext);
  const images = useContext(ImagesContext);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const heroSlides = [
    { image: images.hero_medical, alt: "Massaggio medicale Locarno - EMI Harmony Riazzino" },
    { image: images.hero_relax, alt: "Massaggio rilassante rimborsato cassa malati Locarno" },
    { image: images.service_hotstone, alt: "Massaggio Hot Stones Locarno Ascona Bellinzona" },
  ];

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", onSelect);
    const autoplay = setInterval(() => {
      if (emblaApi) emblaApi.scrollNext();
    }, 5000);
    return () => {
      emblaApi.off("select", onSelect);
      clearInterval(autoplay);
    };
  }, [emblaApi, onSelect]);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) element.scrollIntoView({ behavior: "smooth" });
  };

  const title = language === 'it' ? content?.hero_title_it : content?.hero_title_de;
  const subtitle = language === 'it' ? content?.hero_subtitle_it : content?.hero_subtitle_de;
  const cta = language === 'it' ? content?.hero_cta_it : content?.hero_cta_de;

  return (
    <section data-testid="hero-section" className="relative">
      <div className="embla" ref={emblaRef}>
        <div className="embla__container">
          {heroSlides.map((slide, index) => (
            <div key={index} className="embla__slide">
              <div className="hero-slide">
                <img src={cld(slide.image, 1600)} srcSet={cldSrcSet(slide.image, [640, 1024, 1600, 1920])} sizes="100vw" alt={slide.alt} loading={index === 0 ? "eager" : "lazy"} fetchPriority={index === 0 ? "high" : undefined} />
                <div className="hero-content">
                  <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }} className="max-w-3xl">
                    {/* Only the first slide carries the page H1; the others repeat the same text */}
                    {React.createElement(index === 0 ? 'h1' : 'p', { className: "text-4xl sm:text-5xl lg:text-6xl text-white font-serif mb-6 tracking-tight" }, title || 'Massaggio Medicale & Benessere')}
                    <p className="text-lg md:text-xl text-white/90 mb-8 font-light">
                      {subtitle || 'Riconosciuta da oltre 30 casse malati complementari (LCA). Rimborso secondo la tua polizza.'}
                    </p>
                    <button data-testid="hero-cta" onClick={() => scrollToSection("services")} className="btn-primary">
                      {cta || 'Scopri i Servizi'}
                      <ChevronDown className="ml-2" size={18} />
                    </button>
                  </motion.div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="embla__dots">
        {heroSlides.map((_, index) => (
          <button key={index} data-testid={`hero-dot-${index}`} className={`embla__dot ${index === selectedIndex ? "embla__dot--selected" : ""}`} onClick={() => emblaApi?.scrollTo(index)} />
        ))}
      </div>
    </section>
  );
};

// Intro Section
const IntroSection = () => {
  const { language, localePath } = useLanguage();
  const content = useContext(ContentContext);
  const images = useContext(ImagesContext);

  const title = language === 'it' ? content?.intro_title_it : content?.intro_title_de;
  const highlight = language === 'it' ? content?.intro_highlight_it : content?.intro_highlight_de;
  const text1 = language === 'it' ? content?.intro_text1_it : content?.intro_text1_de;
  const text2 = language === 'it' ? content?.intro_text2_it : content?.intro_text2_de;
  const duration = language === 'it' ? content?.intro_duration_it : content?.intro_duration_de;

  return (
    <section data-testid="intro-section" className="section-padding bg-white">
      <div className="container-custom">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={fadeInUp} className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-pink-900 mb-8 tracking-tight">{title}</h2>
        </motion.div>
        <div className="grid md:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
            <p className="text-xl md:text-2xl text-pink-800 font-serif italic leading-relaxed mb-6">{highlight}</p>
            <p className="text-neutral-600 mb-4 text-left">{text1}</p>
            <p className="text-neutral-600 text-left">{text2}</p>
            <Link to={localePath("/rimborso")} className="inline-flex items-center gap-1 mt-4 text-sm font-medium text-pink-700 hover:text-pink-900 transition-colors" data-testid="intro-rimborso-link">
              {language === 'it' ? 'Verifica rimborso cassa malattia' : 'Krankenkassen-Rückerstattung prüfen'} <ChevronDown className="w-4 h-4 rotate-[-90deg]" />
            </Link>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="relative">
            <img src={cld(images.service_facial, 900)} alt="Riflessologia facciale Dien Chan a Locarno - EMI Harmony" className="w-full h-auto rounded-lg shadow-xl" />
            <div className="absolute -bottom-4 -right-4 bg-pink-700 text-white p-4 md:p-6 rounded-lg">
              <p className="text-sm font-medium">{duration}</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const { language } = useLanguage();
  const content = useContext(ContentContext);
  const images = useContext(ImagesContext);
  const title = language === 'it' ? content?.services_title_it : content?.services_title_de;
  const services = content?.services || [];

  const serviceImages = [
    images.hero_medical,
    images.hero_relax,
    images.service_facial,
    images.service_lomi,
    images.service_plantar,
    images.service_hotstone,
  ];

  const pretreatmentTexts = {
    it: {
      button: "Modulo Pre-Trattamento",
      description: "Compila il modulo per la raccolta dati personali e lo stato di salute prima del vostro primo appuntamento"
    },
    de: {
      button: "Vorbehandlungs-Formular",
      description: "Füllen Sie das Formular zur Erfassung persönlicher Daten und Ihres Gesundheitszustands vor Ihrem ersten Termin aus"
    }
  };

  const texts = pretreatmentTexts[language] || pretreatmentTexts.it;

  return (
    <section id="services" data-testid="services-section" className="section-padding bg-neutral-50">
      <div className="container-custom">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-pink-900 tracking-tight">{title}</h2>
        </motion.div>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {services.map((service, index) => (
            <motion.div key={index} variants={fadeInUp} data-testid={`service-card-${index}`} className="service-card bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="aspect-[4/3] overflow-hidden">
                <img src={cld(serviceImages[index], 700)} alt={`${language === 'it' ? service.name_it : service.name_de} a Locarno Riazzino - EMI Harmony`} className="service-image w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-serif text-pink-900 mb-3">{language === 'it' ? service.name_it : service.name_de}</h3>
                <p className="text-neutral-600 text-sm text-left">{language === 'it' ? service.description_it : service.description_de}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Pre-treatment Form Button */}
        <motion.div 
          initial="hidden" 
          whileInView="visible" 
          viewport={{ once: true }} 
          variants={fadeInUp}
          className="mt-16 text-center"
        >
          <a
            href="https://paz.emi-harmony.ch"
            target="_blank"
            rel="nofollow noopener noreferrer"
            data-testid="pretreatment-button"
            className="inline-flex items-center justify-center px-8 py-4 text-sm font-bold uppercase tracking-widest text-white bg-pink-700 hover:bg-pink-800 transition-all duration-300 rounded-full shadow-lg hover:shadow-xl"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {texts.button}
          </a>
          <p className="mt-4 text-neutral-500 text-sm max-w-md mx-auto">
            {texts.description}
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// Packages Section
const PackagesSection = () => {
  const { language } = useLanguage();
  const content = useContext(ContentContext);
  const title = language === 'it' ? content?.packages_title_it : content?.packages_title_de;
  const prepayment = language === 'it' ? content?.packages_prepayment_it : content?.packages_prepayment_de;
  const sessions = language === 'it' ? content?.packages_sessions_it : content?.packages_sessions_de;
  const packages = content?.packages || [];

  return (
    <section id="packages" data-testid="packages-section" className="section-padding bg-white">
      <div className="container-custom">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-pink-900 tracking-tight">{title}</h2>
        </motion.div>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {packages.map((pkg, index) => (
            <motion.div key={index} variants={fadeInUp} data-testid={`package-card-${index}`} className={`package-card bg-white border rounded-lg p-8 text-center ${index === 1 ? "featured border-amber-400 border-2" : "border-pink-100"}`}>
              {index === 1 && <span className="inline-block bg-amber-400 text-amber-900 text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-4">Popular</span>}
              <h3 className="text-2xl font-serif text-pink-900 mb-4">{language === 'it' ? pkg.name_it : pkg.name_de}</h3>
              <p className="text-neutral-600 mb-6 text-sm">{language === 'it' ? pkg.description_it : pkg.description_de}</p>
              <div className="mb-4">
                <span className="text-4xl font-serif text-pink-800">CHF {pkg.price}</span>
              </div>
              <p className="text-xs text-neutral-500">(5 {sessions} - {prepayment})</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const { language } = useLanguage();
  const content = useContext(ContentContext);
  const images = useContext(ImagesContext);
  const title = language === 'it' ? content?.about_title_it : content?.about_title_de;
  const credentials = language === 'it' ? content?.about_credentials_it : content?.about_credentials_de;
  const credentials2 = language === 'it' ? content?.about_credentials2_it : content?.about_credentials2_de;
  const registries = language === 'it' ? content?.about_registries_it : content?.about_registries_de;
  const bio = language === 'it' ? content?.about_bio_it : content?.about_bio_de;

  return (
    <section id="about" data-testid="about-section" className="section-padding bg-pink-50">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="order-2 lg:order-1">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-pink-900 mb-8 tracking-tight">{title}</h2>
            <div className="mb-6">
              <h3 className="text-2xl font-serif text-pink-800 mb-2">{content?.about_name}</h3>
              <p className="text-pink-700 font-medium">{credentials}</p>
              <p className="text-pink-700">{credentials2}</p>
              <p className="text-neutral-500 text-sm mt-1">{registries}</p>
            </div>
            <div className="space-y-4">
              {bio?.split('\n\n').map((paragraph, index) => (
                <p key={index} className="text-neutral-600 text-left">{paragraph}</p>
              ))}
            </div>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="order-1 lg:order-2">
            <div className="about-image-wrapper relative max-w-md mx-auto lg:mx-0">
              <img src={cld(images.profile, 900)} alt="Emilija Nikolova - Massaggiatrice Medicale CRS Locarno Riazzino" className="w-full h-auto rounded-lg shadow-xl" data-testid="about-image"
                onError={(e) => { e.target.onerror = null; e.target.src = defaultImages.hero_medical; }} />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// FAQ Section - same data as the FAQPage schema and server HTML (backend/seo_content.py)
const FAQSection = () => {
  const { language, localePath } = useLanguage();
  const items = faqData[language] || faqData.it;

  return (
    <section id="faq" data-testid="faq-section" className="section-padding bg-white">
      <div className="container-custom max-w-3xl">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="text-center mb-10">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-pink-900 tracking-tight">
            {language === 'it' ? 'Domande Frequenti' : 'Häufige Fragen'}
          </h2>
        </motion.div>
        <div className="space-y-4">
          {items.map((item, index) => (
            <details key={index} className="group border border-pink-100 rounded-lg p-5" data-testid={`faq-item-${index}`}>
              <summary className="cursor-pointer list-none flex items-start justify-between gap-4 font-serif text-lg text-pink-900">
                {item.q}
                <ChevronDown className="w-5 h-5 mt-1 shrink-0 text-pink-600 transition-transform group-open:rotate-180" />
              </summary>
              <p className="mt-3 text-neutral-600 text-left">{item.a}</p>
            </details>
          ))}
        </div>
        <p className="text-center mt-8">
          <Link to={localePath("/rimborso")} className="text-sm font-medium text-pink-700 hover:text-pink-900 transition-colors">
            {language === 'it' ? 'Tabella rimborsi casse malati' : 'Rückerstattungstabelle der Krankenkassen (auf Italienisch)'}
          </Link>
        </p>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => {
  const { language, localePath } = useLanguage();
  const content = useContext(ContentContext);
  const images = useContext(ImagesContext);
  const title = language === 'it' ? content?.cta_title_it : content?.cta_title_de;
  const subtitle = language === 'it' ? content?.cta_subtitle_it : content?.cta_subtitle_de;

  return (
    <section data-testid="cta-section" className="relative py-24 bg-cover bg-center bg-fixed" style={{ backgroundImage: `url(${cld(images.service_lomi, 1920)})` }}>
      <div className="absolute inset-0 bg-pink-900/70"></div>
      <div className="container-custom relative z-10 text-center">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-white mb-4 tracking-tight">{title}</h2>
          <p className="text-xl text-white/80 font-light mb-6">{subtitle}</p>
          <Link to={localePath("/dove-mi-trovate")} className="inline-flex items-center gap-2 text-sm font-medium text-white/90 hover:text-white border border-white/30 px-5 py-2.5 rounded-full transition-colors hover:border-white/60" data-testid="cta-studio-link">
            {language === 'it' ? 'Come raggiungerci a Riazzino-Locarno' : 'So erreichen Sie uns in Riazzino-Locarno'}
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const { language } = useLanguage();
  const content = useContext(ContentContext);
  const title = language === 'it' ? content?.contact_title_it : content?.contact_title_de;
  const subtitle = language === 'it' ? content?.contact_subtitle_it : content?.contact_subtitle_de;
  const whatsappLabel = language === 'it' ? content?.contact_whatsapp_it : content?.contact_whatsapp_de;
  const footer = language === 'it' ? content?.footer_text_it : content?.footer_text_de;

  return (
    <section id="contact" data-testid="contact-section" className="section-padding bg-white">
      <div className="container-custom">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-pink-900 mb-4 tracking-tight">{title}</h2>
          <p className="text-neutral-600 text-lg">{subtitle}</p>
        </motion.div>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="flex flex-col sm:flex-row justify-center items-center gap-4 sm:gap-6 mb-8">
          <motion.a variants={fadeInUp} href="#" onClick={(e) => { e.preventDefault(); window.location.href = `tel:${CONTACT.phoneLink}`; }} data-testid="contact-phone" className="btn-primary flex items-center gap-3 w-full sm:w-auto justify-center" rel="nofollow">
            <Phone size={20} />
            {CONTACT.phone}
          </motion.a>
          <motion.a variants={fadeInUp} href="#" onClick={(e) => { e.preventDefault(); window.open(`https://wa.me/${CONTACT.whatsapp}`, '_blank', 'noopener,noreferrer'); }} rel="nofollow" data-testid="contact-whatsapp" className="inline-flex items-center justify-center px-8 py-4 text-sm font-bold uppercase tracking-widest text-white bg-green-600 hover:bg-green-700 transition-all duration-300 rounded-full gap-3 w-full sm:w-auto">
            <MessageCircle size={20} />
            {whatsappLabel || 'WhatsApp'}
          </motion.a>
        </motion.div>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="flex items-center justify-center gap-2 text-neutral-600 mb-6">
          <Mail size={18} />
          <a href="#" onClick={(e) => { e.preventDefault(); window.location.href = `mailto:${CONTACT.email}`; }} className="hover:text-pink-700 transition-colors" rel="nofollow" data-testid="contact-email">{CONTACT.email}</a>
        </motion.div>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="flex items-center justify-center gap-2 text-neutral-500 mb-16">
          <MapPin size={18} />
          <span>{CONTACT.address[language]}</span>
        </motion.div>
        <div className="border-t border-pink-100 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-4">
              <img src={LOGO_URL} alt="EMI Harmony" className="h-8 w-auto opacity-70" />
              <p className="text-neutral-500 text-sm">{footer}</p>
            </div>
            <div className="flex items-center gap-4 text-sm text-neutral-500">
              <a href="https://rme.ch/terapeuta/emilianikolova" target="_blank" rel="noopener noreferrer" className="hover:text-pink-700 transition-colors" data-testid="footer-rme">RME</a>
              <span>•</span>
              <a href="https://www.nareg.ch/Person/Summary/28943" target="_blank" rel="noopener noreferrer" className="hover:text-pink-700 transition-colors" data-testid="footer-nareg">NAREG</a>
            </div>
          </div>
          <LegalLinks />
          {/* SEO: Zona Servita */}
          <div className="mt-6 pt-5 border-t border-pink-50">
            <h3 className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-2 text-center">
              {language === 'it' ? 'Massaggi Medicali - Zona Servita' : 'Medizinische Massage - Einzugsgebiet'}
            </h3>
            <p className="text-xs text-neutral-400 text-center leading-relaxed" data-testid="footer-zones">
              {language === 'it'
                ? 'Locarno | Riazzino | Ascona | Bellinzona | Minusio | Muralto | Losone | Tenero | Gordola | Giubiasco | Distretto di Locarno, Ticino'
                : 'Locarno | Riazzino | Ascona | Bellinzona | Minusio | Muralto | Losone | Tenero | Gordola | Giubiasco | Bezirk Locarno, Tessin'}
            </p>
            <p className="text-xs text-neutral-400 text-center mt-1">
              {language === 'it'
                ? 'Massaggio medicale riconosciuto dalle casse malati | Riflessologia plantare | Hot Stones | Lomi Lomi | Casse malati LCA'
                : 'Von Krankenkassen anerkannte medizinische Massage | Fussreflexzonenmassage | Hot Stones | Lomi Lomi | Zusatzversicherung VVG'}
            </p>
          </div>
          <div className="mt-6 pt-5 border-t border-pink-50">
            <p className="text-xs text-neutral-400 text-center leading-relaxed max-w-3xl mx-auto" data-testid="footer-disclaimer">
              {language === 'it'
                ? 'Le informazioni sui rimborsi delle casse malattie riportate su questo sito sono fornite a titolo indicativo e potrebbero non essere aggiornate o complete. Le condizioni di rimborso variano in base alla polizza individuale, al prodotto assicurativo e alle modifiche contrattuali dell\'assicuratore. Si declina ogni responsabilità per eventuali inesattezze. Si raccomanda di verificare sempre le condizioni direttamente con la propria cassa malattia prima di iniziare un trattamento.'
                : 'Die auf dieser Website aufgeführten Informationen zur Kostenübernahme durch Krankenkassen dienen nur als Orientierung und sind möglicherweise nicht aktuell oder vollständig. Die Erstattungsbedingungen variieren je nach individueller Police, Versicherungsprodukt und Vertragsänderungen des Versicherers. Es wird jede Haftung für etwaige Ungenauigkeiten ausgeschlossen. Es wird empfohlen, die Bedingungen immer direkt mit Ihrer Krankenkasse zu prüfen, bevor Sie eine Behandlung beginnen.'}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Main Content with data fetching
const MainContent = () => {
  const { language } = useLanguage();
  const [content, setContent] = useState(null);
  const [siteImages, setSiteImages] = useState(defaultImages);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [contentRes, imagesRes] = await Promise.all([
          fetch(`${API_URL}/content`),
          fetch(`${API_URL}/site-images`)
        ]);
        if (contentRes.ok) {
          setContent(await contentRes.json());
        }
        if (imagesRes.ok) {
          const imgData = await imagesRes.json();
          setSiteImages(prev => ({ ...prev, ...imgData }));
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    setPageMeta(language === 'de' ? DEFAULT_META_DE : DEFAULT_META);
  }, [language]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-pink-50">
        <div className="w-12 h-12 border-4 border-pink-200 border-t-pink-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <ContentContext.Provider value={content}>
      <ImagesContext.Provider value={siteImages}>
        <div className="min-h-screen">
          <Header />
          <main>
            <HeroSection />
            <IntroSection />
            <ServicesSection />
            <PackagesSection />
            <AboutSection />
            <FAQSection />
            <VoucherBanner />
            <CTASection />
            <ContactSection />
          </main>
        </div>
      </ImagesContext.Provider>
    </ContentContext.Provider>
  );
};

// Dove Mi Trovate Page Wrapper
const DoveMiTrovatePage = () => {
  const { language } = useLanguage();
  return (
    <div className="min-h-screen">
      <Header />
      <main>
        <DoveMiTrovate language={language} />
        <LegalLinks />
      </main>
    </div>
  );
};

// Rimborso Page Wrapper
const RimborsoPage = () => {
  return (
    <div className="min-h-screen">
      <Header />
      <main className="pt-16">
        <RimborsoCassaMalattia />
        <LegalLinks />
      </main>
    </div>
  );
};

// Buoni Regalo Page Wrapper
const BuoniRegaloPage = () => {
  return (
    <div className="min-h-screen">
      <Header />
      <main className="pt-16">
        <BuoniRegalo />
        <LegalLinks />
      </main>
    </div>
  );
};

// Verify Voucher Page Wrapper (public scan from QR)
const VerifyVoucherPage = () => {
  return (
    <div className="min-h-screen">
      <Header />
      <main className="pt-16">
        <VerifyVoucher />
      </main>
    </div>
  );
};

// Admin Page
const AdminPage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    const credentials = sessionStorage.getItem('adminCredentials');
    if (credentials) setIsLoggedIn(true);
  }, []);

  const handleLogout = () => {
    sessionStorage.removeItem('adminCredentials');
    setIsLoggedIn(false);
  };

  if (!isLoggedIn) {
    return <AdminLogin onLogin={() => setIsLoggedIn(true)} />;
  }

  return <AdminPanel onLogout={handleLogout} />;
};

// Links to Privacy / Impressum, shown at the bottom of every public page
const LegalLinks = () => {
  const { language, localePath } = useLanguage();
  return (
    <p className="text-xs text-neutral-400 text-center py-6" data-testid="legal-links">
      <Link to={localePath("/privacy")} className="hover:text-pink-700 transition-colors">{language === 'it' ? 'Privacy' : 'Datenschutz'}</Link>
      <span className="mx-2">•</span>
      <Link to={localePath("/impressum")} className="hover:text-pink-700 transition-colors">{language === 'it' ? 'Note legali' : 'Impressum'}</Link>
    </p>
  );
};

// Privacy / Impressum page (same data as backend/seo_content.py)
const LegalPage = ({ kind }) => {
  const { language } = useLanguage();
  const page = (legalData[language] || legalData.it)[kind];

  useEffect(() => {
    setPageMeta({
      title: `${page.title} | EMI Harmony`,
      description: page.description,
      path: language === 'de' ? `/de/${kind}` : `/${kind}`,
    });
    return () => { setPageMeta(DEFAULT_META); };
  }, [language, kind, page]);

  return (
    <div className="min-h-screen">
      <Header />
      <main className="pt-28 pb-8">
        <div className="container-custom max-w-3xl">
          <h1 className="text-4xl md:text-5xl font-serif text-pink-900 mb-3">{page.title}</h1>
          {page.updated && <p className="text-sm text-neutral-500 mb-8">{page.updated}</p>}
          {page.sections.map((section, index) => (
            <section key={index} className="mb-8">
              <h2 className="text-2xl font-serif text-pink-900 mb-3">{section.h}</h2>
              {section.p.map((paragraph, i) => (
                <p key={i} className="text-neutral-600 mb-2 text-left">{paragraph}</p>
              ))}
            </section>
          ))}
        </div>
        <LegalLinks />
      </main>
    </div>
  );
};

// 404 Page
const NotFoundPage = () => {
  const { language, localePath } = useLanguage();

  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    document.title = '404 - Pagina non trovata | EMI Harmony';
    return () => { document.head.removeChild(meta); document.title = 'EMI Harmony | Massaggio Medicale Locarno-Riazzino - Massaggiatrice CRS'; };
  }, []);
  return (
    <div className="min-h-screen">
      <Header />
      <main className="pt-24 pb-16">
        <div className="container-custom text-center">
          <h1 className="text-8xl sm:text-9xl font-serif text-pink-200 font-bold mb-4" data-testid="404-title">404</h1>
          <h2 className="text-2xl sm:text-3xl font-serif text-pink-900 mb-4">
            {language === 'it' ? 'Pagina non trovata' : 'Seite nicht gefunden'}
          </h2>
          <p className="text-neutral-600 mb-8 max-w-md mx-auto">
            {language === 'it'
              ? 'La pagina che cerchi non esiste o è stata spostata. Torna alla homepage per scoprire i nostri trattamenti.'
              : 'Die gesuchte Seite existiert nicht oder wurde verschoben. Kehren Sie zur Startseite zurück.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={localePath("/")} className="btn-primary inline-flex items-center justify-center gap-2" data-testid="404-home-link">
              {language === 'it' ? 'Torna alla Homepage' : 'Zurück zur Startseite'}
            </Link>
            <Link to={localePath("/dove-mi-trovate")} className="inline-flex items-center justify-center px-6 py-3 text-sm font-medium text-pink-700 border border-pink-300 rounded-full hover:bg-pink-50 transition-colors" data-testid="404-studio-link">
              {language === 'it' ? 'Lo Studio' : 'Das Studio'}
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

// Redirect handler component for client-side redirects
const RedirectHandler = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    const checkRedirect = async () => {
      try {
        const resp = await fetch(`${API_URL}/redirects/all`);
        if (resp.ok) {
          const redirects = await resp.json();
          const match = redirects.find(r => r.source === location.pathname);
          if (match) {
            if (match.destination.startsWith('http')) {
              window.location.href = match.destination;
            } else {
              navigate(match.destination, { replace: true });
            }
            return;
          }
        }
      } catch { /* ignore */ }
      setChecked(true);
    };
    checkRedirect();
  }, [location.pathname, navigate]);

  if (!checked) return null;
  return <NotFoundPage />;
};

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<MainContent />} />
          <Route path="/de" element={<MainContent />} />
          <Route path="/rimborso" element={<RimborsoPage />} />
          <Route path="/dove-mi-trovate" element={<DoveMiTrovatePage />} />
          <Route path="/de/dove-mi-trovate" element={<DoveMiTrovatePage />} />
          <Route path="/buoni-regalo" element={<BuoniRegaloPage />} />
          <Route path="/de/buoni-regalo" element={<BuoniRegaloPage />} />
          <Route path="/privacy" element={<LegalPage kind="privacy" />} />
          <Route path="/de/privacy" element={<LegalPage kind="privacy" />} />
          <Route path="/impressum" element={<LegalPage kind="impressum" />} />
          <Route path="/de/impressum" element={<LegalPage kind="impressum" />} />
          <Route path="/verify/:code" element={<VerifyVoucherPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="*" element={<RedirectHandler />} />
        </Routes>
        </Suspense>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
